import * as React from 'react';

import { LiveViewPrompt } from './interfaces';

interface PromptCirclesProps {
  prompts: LiveViewPrompt[],
  totalPrompts: number|null,
  stage?: string|null,
  // LiveViewRow.progressPending
  pending?: boolean,
  // LiveViewRow.completed
  completed?: boolean,
}

const STAGE_LABELS = {
  directions: 'Reading the Directions',
  onboarding: 'Onboarding',
  reading: 'Reading & Highlighting Phase',
  proofreading: 'Editing Passage Phase',
};

// A Proofreader passage with no follow-up questions finishes the activity
export const EDITING_COMPLETED_LABEL = 'Editing Completed';

const MAX_REMAINING_CIRCLES = 200;

// The circles tell optimal from not optimal by colour alone, so the label
// spells out both counts
export const promptCirclesLabel = (prompts: LiveViewPrompt[], totalPrompts: number|null): string => {
  const optimalCount = prompts.filter(prompt => prompt.optimal).length;
  const completed = totalPrompts === null ? `${prompts.length} prompts completed` : `${prompts.length} of ${totalPrompts} prompts completed`;
  return `${completed}: ${optimalCount} optimal, ${prompts.length - optimalCount} not optimal`;
};

const PromptCircles = ({ prompts, totalPrompts, stage, pending, completed, }: PromptCirclesProps) => {
  if (completed && stage === 'proofreading') {
    return <span className="prompt-circles no-prompt-data">{EDITING_COMPLETED_LABEL}</span>;
  }

  // A finished row's stage is where it stopped, not where the student is
  if (!completed && stage && STAGE_LABELS[stage]) {
    return <span className="prompt-circles no-prompt-data">{STAGE_LABELS[stage]}</span>;
  }

  // Tools with no discrete question flow report no prompts
  if (totalPrompts === null && prompts.length === 0) {
    return <span className="prompt-circles no-prompt-data">—</span>;
  }

  if (pending) {
    return <span className="prompt-circles no-prompt-data loading">Loading…</span>;
  }

  // totalPrompts originates from an unauthenticated endpoint, so bound the allocation
  const remainingCount = totalPrompts === null ? 0 : Math.min(Math.max(totalPrompts - prompts.length, 0), MAX_REMAINING_CIRCLES);

  return (
    <span aria-label={promptCirclesLabel(prompts, totalPrompts)} className="prompt-circles" role="img">
      <span className="prompt-dots">
        {prompts.map((prompt, i) => (
          <span
            aria-hidden="true"
            className={`prompt-circle ${prompt.optimal ? 'optimal' : 'sub-optimal'}`}
            key={prompt.id || i}
          />
        ))}
        {/* The first unanswered prompt is the current one, unless the student finished */}
        {Array.from({ length: remainingCount }).map((_, i) => (
          <span aria-hidden="true" className={`prompt-circle incomplete ${i === 0 && !completed ? 'current' : ''}`} key={`incomplete-${i}`} />
        ))}
      </span>
      {totalPrompts !== null && <span className="prompt-count">{prompts.length}/{totalPrompts}</span>}
    </span>
  );
};

export default PromptCircles;
